import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useContent } from '../contexts/ContentContext';

const Formations = () => {
  const { getContent } = useContent();
  const pageHeader = getContent('formations.pageHeader') || {};
  const formationsGrid = getContent('formations.formationsGrid') || {};
  const calendar = getContent('formations.calendar') || {};
  const registrationForm = getContent('formations.registrationForm') || {};
  const formationsCta = getContent('formations.cta') || {};
  
  const formations = formationsGrid.formations || [];

  useEffect(() => {
    // Simple scroll animation for formation cards
    const cards = document.querySelectorAll('.formation-card');
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.style.animationPlayState = 'running';
        }
      });
    });

    cards.forEach(card => observer.observe(card));

    // Handle hash navigation on page load
    if (window.location.hash) {
      setTimeout(() => {
        const targetId = window.location.hash.substring(1);
        const element = document.getElementById(targetId) || document.querySelector(`.${targetId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }

    return () => {
      cards.forEach(card => observer.unobserve(card));
    };
  }, []);

  return (
    <div className="formations-page">
      {/* Page Header */}
      <section className="page-header">
        <div className="container">
          <div className="page-header-content">
            <h1>{pageHeader.title || 'Nos Formations'}</h1>
            <p>{pageHeader.subtitle || '9 spécialités dentaires pour maîtriser les techniques les plus avancées de la dentisterie moderne'}</p>
            {pageHeader.buttons && pageHeader.buttons.length > 0 && (
              <div className="page-header-buttons">
                {pageHeader.buttons.map((button, index) => (
                  <Link
                    key={index}
                    to={button.link}
                    className={`btn ${button.type === 'primary' ? 'btn-primary' : 'btn-outline-white'}`}
                  >
                    {button.icon && <i className={button.icon}></i>}
                    {button.text}
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Formations Grid */}
      <section className="formations-grid-section" id="formations-grid-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{formationsGrid.title || '9 Formations d\'Excellence'}</h2>
            <p className="section-subtitle">
              {formationsGrid.subtitle || 'Découvrez notre gamme complète de formations spécialisées'}
            </p>
          </div>
          
          <div className="formations-grid">
            {formations.map((formation, index) => (
              <div key={formation.id} className={`formation-card-detailed formation-${formation.color}`} id={formation.id}>
                {/* Formation Header with Photos */}
                <div className="formation-header-detailed">
                  <div className="formation-photo">
                    {formation.formationPhoto ? (
                      <img src={formation.formationPhoto} alt={formation.title} />
                    ) : (
                      <div className="formation-photo-placeholder">
                        <i className={formation.icon}></i>
                      </div>
                    )}
                  </div>
                  
                  <div className="formation-title-section">
                    <div className="formation-level-overlay">
                      {formation.level.map((level, idx) => (
                        <span key={idx} className="level-badge">{level}</span>
                      ))}
                    </div>
                    <h3>{formation.title}</h3>
                    <p className="formation-subtitle">{formation.subtitle}</p>
                    
                    {/* Available Spots Display */}
                    {formation.sessions && formation.sessions.length > 0 && (
                      <div className="formation-spots-display">
                        <i className="fas fa-users"></i>
                        <span className="spots-text">{formation.sessions[0].spots}</span>
                        <span className="next-session">Prochaine session: {formation.sessions[0].date}</span>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Instructor Section - Left aligned with text and CV button */}
                <div className="formation-instructor-section">
                  <div className="instructor-photo">
                    {formation.instructorPhoto ? (
                      <img src={formation.instructorPhoto} alt={formation.instructor} />
                    ) : (
                      <div className="instructor-photo-placeholder">
                        <i className="fas fa-user-tie"></i>
                      </div>
                    )}
                  </div>
                  <div className="instructor-content">
                    <div className="instructor-info">
                      <span className="instructor-name">{formation.instructor}</span>
                      <span className="instructor-title">{formation.instructorTitle || 'Formateur Expert'}</span>
                      {formation.instructorBio && (
                        <p className="instructor-bio">{formation.instructorBio}</p>
                      )}
                    </div>
                    {formation.instructorCV && (
                      <button 
                        className="btn btn-instructor-cv"
                        onClick={async () => {
                          try {
                            // Fetch the PDF
                            const response = await fetch(formation.instructorCV);
                            const blob = await response.blob();
                            
                            // Create blob URL
                            const blobUrl = URL.createObjectURL(blob);
                            
                            // Open in new tab
                            window.open(blobUrl, '_blank');
                            
                            // Clean up blob URL after a delay
                            setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
                          } catch (error) {
                            console.error('Error loading CV:', error);
                            alert('Erreur lors du chargement du CV');
                          }
                        }}
                      >
                        <i className="fas fa-file-alt"></i>
                        Voir le CV
                      </button>
                    )}
                  </div>
                </div>
                
                {/* Formation Content */}
                <div className="formation-content-detailed">
                  <p className="formation-description">{formation.description}</p>
                  
                  {/* Objectives */}
                  <div className="formation-objectives">
                    <h4><i className="fas fa-bullseye"></i> Objectifs Principaux</h4>
                    <ul className="objectives-list">
                      {(formation.objectives || []).slice(0, 3).map((objective, objIndex) => (
                        <li key={objIndex}>
                          <i className="fas fa-check-circle"></i>
                          <span>{objective}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  {/* Program */}
                  <div className="formation-program">
                    <h4><i className="fas fa-book-open"></i> Programme de la Formation</h4>
                    <div className="program-modules">
                      {(formation.program || []).map((module, modIndex) => (
                        <div key={modIndex} className="program-module">
                          <div className="module-header">
                            <span className="module-name">{module.module}</span>
                            <span className="module-duration">{module.duration}</span>
                          </div>
                          <h5>{module.title}</h5>
                          <ul className="module-content">
                            {(module.content || []).map((item, itemIndex) => (
                              <li key={itemIndex}>{item}</li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Sessions */}
                  <div className="formation-sessions">
                    <h4><i className="fas fa-calendar-alt"></i> Prochaines Sessions</h4>
                    <div className="sessions-list">
                      {formation.sessions.map((session, sessionIndex) => (
                        <div key={sessionIndex} className="session-item">
                          <div className="session-date">
                            <i className="fas fa-calendar"></i>
                            <strong>{session.date}</strong>
                          </div>
                          <div className="session-location">
                            <i className="fas fa-map-marker-alt"></i>
                            <span>{session.location}</span>
                          </div>
                          <div className="session-spots">
                            <span className="spots-badge">{session.spots}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Reviews */}
                  <div className="formation-reviews">
                    <h4><i className="fas fa-star"></i> Avis des Participants</h4>
                    <div className="reviews-list">
                      {(formation.reviews || []).slice(0, 2).map((review, reviewIndex) => (
                        <div key={reviewIndex} className="review-item">
                          <div className="review-header">
                            <span className="reviewer-name">{review.name}</span>
                            <div className="review-rating">
                              {[...Array(5)].map((_, starIndex) => (
                                <i key={starIndex} className={`fas fa-star ${starIndex < review.rating ? 'star-filled' : 'star-empty'}`}></i>
                              ))}
                            </div>
                            <span className="review-date">{review.date}</span>
                          </div>
                          <p className="review-comment">"{review.comment}"</p>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="formation-actions">
                    <a href="#registration-form"
                       className="btn btn-primary btn-full-width"
                       onClick={(e) => {
                         e.preventDefault();
                         document.getElementById('registration-form')?.scrollIntoView({ behavior: 'smooth' });
                       }}>
                      <i className="fas fa-user-plus"></i>
                      S'inscrire à cette Formation
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Calendar Section (moved below formations) */}
      <section className="sessions-calendar" id="calendar">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{calendar.title || 'Calendrier Global des Sessions'}</h2>
            <p className="section-subtitle">
              {calendar.subtitle || 'Vue d\'ensemble de toutes nos prochaines formations'}
            </p>
          </div>

          <div className="calendar-grid">
            {formations.map((formation) => 
              formation.sessions.map((session, sessionIndex) => (
                <div key={`${formation.id}-${sessionIndex}`} className={`calendar-card formation-${formation.color}`}>
                  <div className="calendar-header">
                    <div className="calendar-icon">
                      <i className={formation.icon}></i>
                    </div>
                    <h4>{formation.title}</h4>
              </div>
              
                  <div className="calendar-details">
                    <div className="calendar-date">
                      <i className="fas fa-calendar"></i>
                      <strong>{session.date}</strong>
                    </div>
                    <div className="calendar-location">
                      <i className="fas fa-map-marker-alt"></i>
                      <span>{session.location}</span>
                    </div>
                    <div className="calendar-instructor">
                      <i className="fas fa-chalkboard-teacher"></i>
                      <span>{formation.instructor}</span>
                    </div>
                    <div className="calendar-spots">
                    <i className="fas fa-users"></i>
                      <span className="spots-available">{session.spots}</span>
                  </div>
                </div>
                
                  <div className="calendar-actions">
                    <a href="#registration-form"
                       className="btn btn-primary btn-full"
                       onClick={(e) => {
                         e.preventDefault();
                         document.getElementById('registration-form')?.scrollIntoView({ behavior: 'smooth' });
                       }}>
                      <i className="fas fa-user-plus"></i>
                      S'inscrire
                    </a>
                  </div>
                </div>
              ))
            )}
          </div>
              </div>
      </section>

      {/* Registration Form */}
      <section className="registration-form-section" id="registration-form">
        <div className="container">
          <div className="form-container">
            <div className="form-header">
              <h2>{registrationForm.title || 'Demande d\'Inscription'}</h2>
              <p>{registrationForm.subtitle || 'Remplissez ce formulaire pour recevoir plus d\'informations sur nos formations'}</p>
            </div>
            
            <form className="registration-form">
              {/* Dynamic form fields - first 4 fields in a 2x2 grid */}
              <div className="form-row">
                {(registrationForm.fields || []).slice(0, 2).map((field, index) => (
                  <div key={field.name} className="form-group">
                    <label htmlFor={field.name}>
                      {field.label} {field.required ? '*' : ''}
                    </label>
                    <input 
                      type={field.type} 
                      id={field.name} 
                      name={field.name} 
                      required={field.required}
                      placeholder={field.placeholder || ''}
                    />
                  </div>
                ))}
              </div>
              
              <div className="form-row">
                {(registrationForm.fields || []).slice(2, 4).map((field, index) => (
                  <div key={field.name} className="form-group">
                    <label htmlFor={field.name}>
                      {field.label} {field.required ? '*' : ''}
                    </label>
                    <input 
                      type={field.type} 
                      id={field.name} 
                      name={field.name} 
                      required={field.required}
                      placeholder={field.placeholder || ''}
                    />
                  </div>
                ))}
              </div>
              
              {/* Formation select field */}
              {(registrationForm.fields || []).find(field => field.name === 'formation') && (
                <div className="form-group">
                  <label htmlFor="formation">Formation d'intérêt</label>
                  <select id="formation" name="formation">
                    <option value="">Sélectionnez une formation</option>
                    {formations.map((formation) => (
                      <option key={formation.id} value={formation.title}>{formation.title}</option>
                    ))}
                  </select>
                </div>
              )}
              
              {/* Textarea fields */}
              {(registrationForm.fields || []).filter(field => field.type === 'textarea').map((field) => (
                <div key={field.name} className="form-group">
                  <label htmlFor={field.name}>
                    {field.label} {field.required ? '*' : ''}
                  </label>
                  <textarea 
                    id={field.name} 
                    name={field.name} 
                    rows="4" 
                    placeholder={field.placeholder || ''}
                    required={field.required}
                  ></textarea>
                </div>
              ))}
              
              <div className="form-actions">
                <button type="submit" className="btn btn-primary btn-large">
                  <i className={registrationForm.submitButton?.icon || "fas fa-paper-plane"}></i>
                  {registrationForm.submitButton?.text || 'Envoyer la Demande'}
                </button>
                <p className="form-note">
                  <i className={registrationForm.securityNote?.icon || "fas fa-shield-alt"}></i>
                  {registrationForm.securityNote?.text || 'Vos informations sont sécurisées et ne seront pas partagées'}
                </p>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="cta">
        <div className="container">
          <div className="cta-content">
            <h2>{formationsCta.title || 'Prêt à Transformer Votre Pratique ?'}</h2>
            <p>{formationsCta.subtitle || 'Rejoignez plus de 500 dentistes qui ont fait confiance à DentoXcellence pour leur développement professionnel'}</p>
            <div className="cta-buttons">
              {(formationsCta.buttons || []).map((button, index) => (
                button.link?.startsWith('http') ? (
                  <a key={index}
                     href={button.link} 
                     className={`btn btn-${button.type}`}
                     target="_blank" 
                     rel="noopener noreferrer">
                    <i className={button.icon}></i>
                    {button.text}
                  </a>
                ) : (
                  <Link key={index} to={button.link} className={`btn btn-${button.type}`}>
                    <i className={button.icon}></i>
                    {button.text}
              </Link>
                )
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Formations;